import { useMutation } from '@apollo/client';
import { useNotifications } from '@sixfold/common-ui';
import { renderChildren, Children } from '@sixfold/typed-render-props';
import React from 'react';

import {
  CreateAllVehicleGrantsMutationVariables,
  RevokeAllVehicleGrantsMutationVariables,
  CreateAllVehicleGrantsMutation as CreateAllVehicleGrantsMutationResult,
  RevokeAllVehicleGrantsMutation as RevokeAllVehicleGrantsMutationResult,
  PAGINATION_PAGE_SIZE,
  CompanyRelationshipType,
} from '../../lib/graphql';
import { createAllVehicleGrantsMutation, revokeAllVehicleGrantsMutation, grantRelationshipsQuery } from '../graphql';

interface Props {
  children: Children<{
    createVehicleGrants: (input: CreateAllVehicleGrantsMutationVariables['input']) => Promise<void>;
    revokeVehicleGrants: (input: RevokeAllVehicleGrantsMutationVariables['input']) => Promise<void>;
  }>;
}

export const VehicleGrantMutations: React.FC<Props> = (props) => {
  const notify = useNotifications();
  const [createVehicleGrants] = useMutation<
    CreateAllVehicleGrantsMutationResult,
    CreateAllVehicleGrantsMutationVariables
  >(createAllVehicleGrantsMutation);
  const [revokeVehicleGrants] = useMutation<
    RevokeAllVehicleGrantsMutationResult,
    RevokeAllVehicleGrantsMutationVariables
  >(revokeAllVehicleGrantsMutation);

  return renderChildren(props.children as React.ReactNode, {
    createVehicleGrants: async (input: CreateAllVehicleGrantsMutationVariables['input']) => {
      const response = await createVehicleGrants({
        variables: { input },
        refetchQueries: [
          {
            query: grantRelationshipsQuery,
            variables: {
              companyId: input.grantedByCompany,
              sourceCompanyId: input.grantedByCompany,
              relationshipType: CompanyRelationshipType.SUBCARRIER,
              limit: PAGINATION_PAGE_SIZE,
            },
          },
        ],
      });
      notify.success({
        title: 'Vehicles granted',
      });
      return response;
    },
    revokeVehicleGrants: async (input: RevokeAllVehicleGrantsMutationVariables['input']) => {
      const response = await revokeVehicleGrants({
        variables: { input },
        refetchQueries: [
          {
            query: grantRelationshipsQuery,
            variables: {
              companyId: input.revokedByCompany,
              sourceCompanyId: input.revokedByCompany,
              relationshipType: CompanyRelationshipType.SUBCARRIER,
              limit: PAGINATION_PAGE_SIZE,
            },
          },
        ],
      });
      notify.success({
        title: 'Vehicle grants revoked',
      });
      return response;
    },
  });
};
